import React, { createContext, useContext, useState, useEffect } from 'react';
import client from '../api/client';

const BrandContext = createContext(null);

const DEFAULT_BRAND = {
  app_name:       '',
  tagline:        '',
  primary_color:  '#0099FF',
  dark_color:     '#151515',
  logo_url:       '',
  login_logo_url: '',
  favicon_url:    '',
  hide_name:      false,
};

const STORAGE_KEY = 'dx_brand';

// Darken a hex color by percentage
const darkenHex = (hex, pct = 12) => {
  try {
    const num = parseInt(hex.replace('#',''), 16);
    const r   = Math.max(0, (num >> 16)        - Math.round(255 * pct / 100));
    const g   = Math.max(0, ((num >> 8) & 0xFF)- Math.round(255 * pct / 100));
    const b   = Math.max(0, (num & 0xFF)        - Math.round(255 * pct / 100));
    return `#${((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)}`;
  } catch { return hex; }
};

// Hex to rgba
const hexToRgba = (hex, alpha = 0.12) => {
  try {
    const num = parseInt(hex.replace('#',''), 16);
    return `rgba(${(num>>16)&255},${(num>>8)&255},${num&255},${alpha})`;
  } catch { return `rgba(0,153,255,${alpha})`; }
};

const applyBrandToDOM = (b) => {
  const root    = document.documentElement;
  const primary = b.primary_color || '#0099FF';
  const dark    = b.dark_color    || '#151515';

  root.style.setProperty('--color-primary',       primary);
  root.style.setProperty('--color-primary-hover', darkenHex(primary, 12));
  root.style.setProperty('--color-primary-light', hexToRgba(primary, 0.12));
  root.style.setProperty('--color-dark',          dark);

  if (b.favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel='icon'; document.head.appendChild(link); }
    link.href = b.favicon_url;
  }

  if (b.app_name) document.title = b.app_name;
};

export const BrandProvider = ({ children }) => {
  const [brand,   setBrand]   = useState(() => {
    // ── Restore from localStorage on first render ──────────
    // This prevents the flash of default settings on refresh
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        applyBrandToDOM(parsed);
        return { ...DEFAULT_BRAND, ...parsed };
      }
    } catch {}
    return DEFAULT_BRAND;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrand();
  }, []); // eslint-disable-line

  const fetchBrand = async () => {
    try {
      const { data } = await client.get('/api/settings/brand');
      if (data) {
        const merged = { ...DEFAULT_BRAND, ...data };
        setBrand(merged);
        applyBrandToDOM(merged);
        // ── Persist to localStorage so refresh keeps settings ──
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      }
    } catch {
      // On error, keep whatever is in localStorage / state
      applyBrandToDOM(brand);
    } finally {
      setLoading(false);
    }
  };

  const updateBrand = (newBrand) => {
    const merged = { ...brand, ...newBrand };
    setBrand(merged);
    applyBrandToDOM(merged);
    // ── Always persist on update ───────────────────────────
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  };

  return (
    <BrandContext.Provider value={{ brand, loading, updateBrand, fetchBrand }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
};
